import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, Plus, Trash2, ChevronLeft, GripVertical, Video } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaUpload } from "@/components/site/MediaUpload";
import { CloudflareVideo } from "@/components/site/CloudflareVideo";
import { toast } from "sonner";

const LEVELS = ["Beginner", "Intermediate", "Advanced"];

export default function AdminCourseEditor() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [sections, setSections] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [saving, setSaving] = useState(false);

  const { data: cats } = useQuery({ queryKey: ["admin-categories"], queryFn: async () => (await api.get("/admin/categories")).data });

  const load = async () => {
    const { data } = await api.get(`/admin/courses/${courseId}`);
    setForm(data.course); setSections(data.sections); setLessons(data.lessons);
  };
  useEffect(() => { load(); }, [courseId]);

  if (!form) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveCourse = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/courses/${courseId}`, form);
      qc.invalidateQueries({ queryKey: ["admin-courses"] });
      toast.success("Course saved");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const addSection = async () => { await api.post("/admin/sections", { course_id: courseId, title: "New Section" }); load(); };
  const updateSection = async (id, title) => { await api.patch(`/admin/sections/${id}`, { title }); };
  const delSection = async (id) => { if (confirm("Delete section?")) { await api.delete(`/admin/sections/${id}`); load(); } };

  const addLesson = async (section_id) => { await api.post("/admin/lessons", { course_id: courseId, section_id, title: "New Lesson" }); load(); };
  const saveLesson = async (l) => { await api.patch(`/admin/lessons/${l.id}`, l); toast.success("Lesson saved"); load(); };
  const delLesson = async (id) => { if (confirm("Delete lesson?")) { await api.delete(`/admin/lessons/${id}`); load(); } };

  return (
    <div className="max-w-4xl">
      <button onClick={() => nav("/admin/courses")} className="mb-4 flex items-center gap-1 text-sm text-zinc-400 hover:text-[#DCA846]"><ChevronLeft className="h-4 w-4" /> Back to courses</button>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight">{form.title}</h1>
        <Button data-testid="save-course-btn" onClick={saveCourse} disabled={saving} className="bg-[#DCA846] text-black hover:bg-[#C5963B]">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save</>}
        </Button>
      </div>

      <Tabs defaultValue="details" className="mt-6">
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="pricing">Pricing & Badges</TabsTrigger>
          <TabsTrigger value="curriculum">Curriculum</TabsTrigger>
          <TabsTrigger value="marketing">Marketing</TabsTrigger>
          <TabsTrigger value="seo">SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-6 space-y-5">
          <Field label="Title"><Input data-testid="course-title" value={form.title || ""} onChange={(e) => set("title", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
          <Field label="Short Description"><Input value={form.short_description || ""} onChange={(e) => set("short_description", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
          <Field label="Full Description"><Textarea rows={6} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <MediaUpload testid="cover-upload" label="Cover Image" value={form.cover_image} onChange={(v) => set("cover_image", v)} kind="image" />
            <MediaUpload testid="thumb-upload" label="Thumbnail" value={form.thumbnail} onChange={(v) => set("thumbnail", v)} kind="image" />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Category">
              <Select value={form.category_id || ""} onValueChange={(v) => set("category_id", v)}>
                <SelectTrigger className="bg-zinc-900 border-white/10 text-white"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">{(cats || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Skill Level">
              <Select value={form.skill_level || "Beginner"} onValueChange={(v) => set("skill_level", v)}>
                <SelectTrigger className="bg-zinc-900 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10 text-white">{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          {form.type === "video" && (
            <MediaUpload testid="single-video-upload" label="Video (single video product)" value={form.video_file || form.video_url}
              onChange={(v) => set(String(v).startsWith("http") ? "video_url" : "video_file", v)} kind="video" accept="video/*" />
          )}
        </TabsContent>

        <TabsContent value="pricing" className="mt-6 space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Price ($)"><Input data-testid="course-price" type="number" step="0.01" value={form.price ?? 0} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} className="bg-zinc-900 border-white/10 text-white" /></Field>
            <Field label="Sale Price ($, optional)"><Input type="number" step="0.01" value={form.sale_price ?? ""} onChange={(e) => set("sale_price", e.target.value === "" ? null : parseFloat(e.target.value))} className="bg-zinc-900 border-white/10 text-white" /></Field>
          </div>
          <Field label="Rating"><Input type="number" step="0.1" min="0" max="5" value={form.rating ?? 5} onChange={(e) => set("rating", parseFloat(e.target.value) || 0)} className="bg-zinc-900 border-white/10 text-white" /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Toggle testid="toggle-free" label="Free content" checked={form.is_free} onChange={(v) => set("is_free", v)} />
            <Toggle testid="toggle-published" label="Published" checked={form.is_published} onChange={(v) => set("is_published", v)} />
            <Toggle testid="toggle-featured" label="Featured" checked={form.is_featured} onChange={(v) => set("is_featured", v)} />
            <Toggle testid="toggle-new" label="New" checked={form.is_new} onChange={(v) => set("is_new", v)} />
            <Toggle testid="toggle-popular" label="Popular" checked={form.is_popular} onChange={(v) => set("is_popular", v)} />
            <Toggle testid="toggle-coming" label="Coming Soon" checked={form.is_coming_soon} onChange={(v) => set("is_coming_soon", v)} />
          </div>
        </TabsContent>

        <TabsContent value="curriculum" className="mt-6">
          <div className="flex justify-end"><Button data-testid="add-section-btn" onClick={addSection} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Plus className="mr-1 h-4 w-4" /> Add Section</Button></div>
          <div className="mt-4 space-y-6">
            {sections.length === 0 && <p className="text-zinc-500">Add sections (e.g. Introduction, Clipper Work, Blending) then add lessons.</p>}
            {sections.map((sec) => (
              <div key={sec.id} className="rounded-lg border border-white/10 bg-zinc-900/40 p-4">
                <div className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-zinc-600" />
                  <Input defaultValue={sec.title} onBlur={(e) => updateSection(sec.id, e.target.value)} className="bg-zinc-950 border-white/10 font-display uppercase text-white" />
                  <Button size="sm" variant="ghost" onClick={() => delSection(sec.id)} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="mt-3 space-y-2">
                  {lessons.filter((l) => l.section_id === sec.id).map((l) => <LessonRow key={l.id} lesson={l} onSave={saveLesson} onDelete={delLesson} />)}
                </div>
                <Button size="sm" variant="outline" onClick={() => addLesson(sec.id)} className="mt-3 border-white/15 text-white"><Plus className="mr-1 h-3 w-3" /> Add Lesson</Button>
              </div>
            ))}
            {lessons.filter((l) => !l.section_id).length > 0 && (
              <div className="rounded-lg border border-white/10 bg-zinc-900/40 p-4">
                <p className="mb-3 font-display uppercase text-zinc-400">Unsectioned Lessons</p>
                <div className="space-y-2">{lessons.filter((l) => !l.section_id).map((l) => <LessonRow key={l.id} lesson={l} onSave={saveLesson} onDelete={delLesson} />)}</div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="seo" className="mt-6 space-y-5">
          <Field label="Meta Title"><Input value={form.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
          <Field label="Meta Description"><Textarea rows={3} value={form.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
          <Field label="URL Slug"><Input value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
          <MediaUpload label="Social Sharing Image" value={form.social_image} onChange={(v) => set("social_image", v)} kind="image" />
        </TabsContent>

        <TabsContent value="marketing" className="mt-6 space-y-5">
          <Field label="Subtitle / Hero supporting copy"><Textarea rows={2} value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
          <div className="rounded-lg border border-white/10 bg-zinc-900/30 p-4 space-y-4">
            <p className="font-display uppercase tracking-wide text-[#DCA846]">Instructor</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Instructor Name"><Input value={form.instructor_name || ""} onChange={(e) => set("instructor_name", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
              <Field label="Instructor Title"><Input value={form.instructor_title || ""} onChange={(e) => set("instructor_title", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
            </div>
            <MediaUpload label="Instructor Photo" value={form.instructor_image} onChange={(v) => set("instructor_image", v)} kind="image" />
            <Field label="Instructor Bio"><Textarea rows={4} value={form.instructor_bio || ""} onChange={(e) => set("instructor_bio", e.target.value)} className="bg-zinc-900 border-white/10 text-white" /></Field>
            <Field label="Credibility Highlights (one per line)">
              <Textarea rows={4} value={(form.instructor_highlights || []).join("\n")} onChange={(e) => set("instructor_highlights", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} className="bg-zinc-900 border-white/10 text-white" />
            </Field>
          </div>
          <div className="rounded-lg border border-white/10 bg-zinc-900/30 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-display uppercase tracking-wide text-[#DCA846]">Learning Outcomes (What You'll Master)</p>
              <Button size="sm" variant="outline" onClick={() => set("learning_outcomes", [...(form.learning_outcomes || []), { title: "", text: "" }])} className="border-white/15 text-white"><Plus className="mr-1 h-3 w-3" /> Add</Button>
            </div>
            {(form.learning_outcomes || []).map((o, i) => (
              <div key={i} className="flex gap-2">
                <Input placeholder="Title" value={o.title || ""} onChange={(e) => { const a = [...form.learning_outcomes]; a[i] = { ...a[i], title: e.target.value }; set("learning_outcomes", a); }} className="bg-zinc-900 border-white/10 text-white" />
                <Input placeholder="Description" value={o.text || ""} onChange={(e) => { const a = [...form.learning_outcomes]; a[i] = { ...a[i], text: e.target.value }; set("learning_outcomes", a); }} className="bg-zinc-900 border-white/10 text-white" />
                <Button size="sm" variant="ghost" onClick={() => set("learning_outcomes", form.learning_outcomes.filter((_, x) => x !== i))} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs uppercase tracking-wider text-zinc-400">{label}</Label><div className="mt-1">{children}</div></div>;
}
function Toggle({ label, checked, onChange, testid }) {
  return <div className="flex items-center justify-between rounded-lg border border-white/10 bg-zinc-900/40 px-4 py-3">
    <span className="text-sm">{label}</span><Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} /></div>;
}

function LessonRow({ lesson, onSave, onDelete }) {
  const [l, setL] = useState(lesson);
  const [open, setOpen] = useState(false);
  const set = (k, v) => setL((p) => ({ ...p, [k]: v }));
  return (
    <div className="rounded border border-white/10 bg-zinc-950/60">
      <div className="flex items-center gap-2 p-2">
        <Video className="h-4 w-4 text-zinc-500" />
        <Input value={l.title} onChange={(e) => set("title", e.target.value)} className="h-8 flex-1 bg-transparent border-none text-white" />
        {l.is_free_preview && <span className="rounded bg-emerald-500/10 px-1.5 text-[10px] uppercase text-emerald-400">Preview</span>}
        <button onClick={() => setOpen((o) => !o)} className="text-xs text-[#DCA846]">{open ? "Close" : "Edit"}</button>
        <button onClick={() => onDelete(l.id)} className="text-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
      {open && (
        <div className="space-y-3 border-t border-white/10 p-3">
          <Textarea rows={2} placeholder="Description" value={l.description || ""} onChange={(e) => set("description", e.target.value)} className="bg-zinc-900 border-white/10 text-white" />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input placeholder="Duration (e.g. 8:30)" value={l.duration || ""} onChange={(e) => set("duration", e.target.value)} className="bg-zinc-900 border-white/10 text-white" />
            <Select value={l.video_type || "16:9"} onValueChange={(v) => set("video_type", v)}>
              <SelectTrigger className="bg-zinc-900 border-white/10 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-zinc-950 border-white/10 text-white"><SelectItem value="16:9">Horizontal 16:9</SelectItem><SelectItem value="9:16">Vertical 9:16</SelectItem></SelectContent>
            </Select>
          </div>
          <CloudflareVideo value={l.cf_video_uid} onChange={(uid) => set("cf_video_uid", uid)} />
          <details className="rounded border border-white/10 bg-zinc-950/40 p-2">
            <summary className="cursor-pointer text-xs text-zinc-400">Or use a direct/hosting URL instead (optional)</summary>
            <div className="mt-2">
              <MediaUpload label="Video (upload file or paste YouTube/Vimeo/MP4 URL)" value={l.video_file || l.video_url}
                onChange={(v) => { const key = v && String(v).startsWith("http") ? "video_url" : "video_file"; setL((p) => ({ ...p, video_file: null, video_url: null, [key]: v })); }}
                kind="video" accept="video/*" />
            </div>
          </details>
          <MediaUpload label="Thumbnail / Poster" value={l.thumbnail} onChange={(v) => set("thumbnail", v)} kind="image" />
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!l.is_free_preview} onCheckedChange={(v) => set("is_free_preview", v)} /> Free preview</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!l.is_locked} onCheckedChange={(v) => set("is_locked", v)} /> Locked</label>
          </div>
          <Button size="sm" onClick={() => onSave(l)} className="bg-[#DCA846] text-black hover:bg-[#C5963B]"><Save className="mr-1 h-3 w-3" /> Save Lesson</Button>
        </div>
      )}
    </div>
  );
}
